import React from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericButton from "@ui-components/Button/GenericButton";
import type { ReleaseLicenseCode } from "./request/request";

/**
 * Copy por código de rechazo.
 *
 * El backend decide si una licencia se puede liberar y devuelve el motivo; acá
 * sólo se traduce a algo que el usuario pueda accionar. Vive en un mapa y no en
 * una cadena de condicionales para que agregar un caso sea una entrada.
 */
const REFUSAL_COPY: Record<ReleaseLicenseCode, string> = {
  REFLECTOR_IN_CLUSTER:
    "This reflector belongs to a cluster. Break the cluster up first, then release its license.",
  PAYG_NOT_RELEASABLE:
    "This reflector is licensed pay-as-you-go. Releasing applies to bring-your-own licenses only.",
  ALREADY_RELEASED: "This reflector has no license to release.",
  REFLECTOR_NOT_FOUND: "This reflector no longer exists.",
  RELEASE_FAILED: "The license could not be released. Try again.",
};

export const refusalMessage = (code?: ReleaseLicenseCode): string =>
  (code && REFUSAL_COPY[code]) || REFUSAL_COPY.RELEASE_FAILED;

export interface ReleaseLicenseModalProps {
  isOpen: boolean;
  /** Nombre del reflector, para que el usuario confirme sobre cuál actúa. */
  reflectorName: string;
  /** Serial de la licencia que se va a soltar, cuando se conoce. */
  licenseSerial?: string;
  isReleasing: boolean;
  /** Motivo del rechazo. Presente sólo después de un intento refusado. */
  refusal: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Confirma la liberación y, si el backend la rechaza, explica por qué sin
 * cerrarse: el usuario se entera en el mismo lugar donde pidió la acción.
 */
const ReleaseLicenseModal: React.FC<ReleaseLicenseModalProps> = ({
  isOpen,
  reflectorName,
  licenseSerial,
  isReleasing,
  refusal,
  onConfirm,
  onClose,
}) => (
  <GenericModal
    isOpen={isOpen}
    onClose={onClose}
    title={refusal ? "Cannot release license" : "Release license"}
    size="md"
  >
    <div>
      {refusal ? (
        <p className="text-sm text-semantic-text-primary">{refusal}</p>
      ) : (
        <>
          <p className="mb-4 text-sm text-semantic-text-primary">
            Release {licenseSerial ? <strong>{licenseSerial}</strong> : "the license"} from{" "}
            <strong>{reflectorName}</strong>?
          </p>
          <div className="rounded-lg border border-semantic-border-warning-primary bg-semantic-bg-warning-secondary p-3">
            <p className="text-sm text-semantic-text-warning">
              The reflector is not deleted and the license is not deleted — its seat
              becomes available again. The reflector will stop operating and lose its
              connection.
            </p>
          </div>
        </>
      )}

      <div className="mt-6 flex justify-end gap-2">
        <GenericButton
          variant={refusal ? "brand" : "secondary"}
          label={refusal ? "Close" : "Cancel"}
          onClick={onClose}
          disabled={isReleasing}
        />
        {!refusal && (
          <GenericButton
            variant="brand"
            label={isReleasing ? "Releasing…" : "Release license"}
            onClick={onConfirm}
            disabled={isReleasing}
          />
        )}
      </div>
    </div>
  </GenericModal>
);

export default ReleaseLicenseModal;
